import { Trash } from "@phosphor-icons/react";
import clsx from "clsx";
import { type JSX, useState } from "react";

import {
  ManageSubscriptionDialog,
  StartTrialButton,
} from "features/subscriptions/components";

import { SplitButton } from "common/components/SplitButton";
import { Button } from "common/components/ui/Button";
import Skeleton from "common/components/ui/Skeleton";
import { Tag } from "common/components/ui/Tag";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useCustomer from "common/datahooks/useCustomer";
import usePromotions from "common/datahooks/usePromotions";
import useWorkspaceDetails from "common/datahooks/useWorkspaceDetails";
import useLicensedAccounts from "common/hooks/useLicensedAccounts";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import SubscriptionCardSeatsSummary from "../../../SubscriptionCardSeatsSummary";
import SubscriptionDetailsCard from "../../../SubscriptionDetailsCard";
import NonRenewingTag from "../NonRenewingTag";
import RemoveChangeButton from "../RemoveChangeButton";
import SubscriptionInfo from "../SubscriptionInfo";
import WorkspaceSubRenewalPrice from "../WorkspaceSubRenewalPrice";
import CancelSubscriptionDialog from "./CancelSubscriptionDialog";
import ScheduledChangeTag from "./ScheduledChangeTag";

export default function User() {
  const isDesktop = useTwBreakpoint("lg");
  const [isManageOpen, setIsManageOpen] = useState(false);
  const [isCancelOpen, setIsCancelOpen] = useState(false);

  const {
    id: workspaceId,
    subscription_status,
    license_count: licenseCount,
  } = useSelectedWorkspaceContext();
  const { workspace, isLoadingWorkspace } = useWorkspaceDetails(workspaceId);
  const { agencyPlan } = usePromotions();
  const { customerInfo } = useCustomer();
  const { outreachLicensedAccounts } = useLicensedAccounts(workspaceId);

  const buttonSize = isDesktop ? "sm" : "md";

  let priceComponent: JSX.Element;
  let tagComponent = <Tag variant="disabled">Inactive</Tag>;
  let buttonComponent = customerInfo.had_trial ? (
    <Button
      size={buttonSize}
      onClick={() => {
        setIsManageOpen(true);
      }}
    >
      Activate subscription
    </Button>
  ) : (
    <StartTrialButton size={buttonSize} />
  );

  if (subscription_status) {
    if (isLoadingWorkspace) {
      buttonComponent = (
        <Skeleton className={clsx(isDesktop ? "h-8 w-40" : "h-10 w-40")} />
      );
      tagComponent = <Skeleton className="w-15 h-6" />;
    } else if (workspace) {
      const { subscription, licenses } = workspace;
      const { cancelled_at, status } = subscription;

      const hasChanges = licenses.some((license) => license.expires_at);

      if (status === "non_renewing" || cancelled_at * 1000 > Date.now()) {
        buttonComponent = (
          <RemoveChangeButton subscription={subscription} type="cancellation" />
        );
        tagComponent = <NonRenewingTag />;
      } else if (hasChanges) {
        tagComponent = <ScheduledChangeTag />;
        buttonComponent = (
          <RemoveChangeButton subscription={subscription} type="change" />
        );
      } else if (status === "cancelled") {
        buttonComponent = (
          <Button
            variant="default-purple"
            size={buttonSize}
            onClick={() => setIsManageOpen(true)}
          >
            Reactivate subscription
          </Button>
        );
      } else {
        tagComponent =
          subscription_status === "in_trial" ? (
            <Tag variant="secondary">7-day free trial</Tag>
          ) : (
            <Tag variant="success">Active</Tag>
          );
        priceComponent = (
          <WorkspaceSubRenewalPrice
            subscriptionId={subscription.id}
            pricingPeriodInfo={subscription.item_price}
          />
        );
        buttonComponent =
          agencyPlan && agencyPlan.subscription.status === "active" ? (
            <Button
              variant="outline-danger"
              onClick={() => setIsCancelOpen(true)}
              size={buttonSize}
            >
              Cancel subscription
            </Button>
          ) : (
            <SplitButton
              size={buttonSize}
              onClick={() => setIsManageOpen(true)}
              dropdownMenuItems={[
                {
                  label: "Cancel subscription",
                  icon: Trash,
                  onSelect: () => setIsCancelOpen(true),
                  variant: "danger",
                },
              ]}
            >
              Manage subscription
            </SplitButton>
          );
      }
    }
  }

  const mainComponent = subscription_status !== "cancelled" && (
    <SubscriptionCardSeatsSummary
      accounts={outreachLicensedAccounts}
      availableSeatsCount={licenseCount}
    />
  );

  return (
    <>
      <SubscriptionDetailsCard
        plan="user"
        footerComponent={
          <SubscriptionInfo
            isLoading={isLoadingWorkspace}
            subscription={workspace?.subscription}
            buttonComponent={buttonComponent}
          />
        }
        tagComponent={tagComponent}
        priceComponent={priceComponent}
        mainComponent={mainComponent}
      />
      <ManageSubscriptionDialog
        plan="user"
        isOpen={isManageOpen}
        setIsOpen={setIsManageOpen}
      />
      <CancelSubscriptionDialog
        isOpen={isCancelOpen}
        setIsOpen={setIsCancelOpen}
        shouldShowSurvey={!agencyPlan}
      />
    </>
  );
}
